import React from 'react'
import { Button } from '../button/Button'
import { LoadingButton } from '../button/LoadingButton'
import { cn } from '../../lib/utils'

export interface SettingsSaveBarProps {
  /** Show the bar. Pass the form's dirty flag. */
  visible: boolean
  onSave: () => void
  /** Omit to hide the discard button (only where there is nothing to revert to). */
  onDiscard?: () => void
  /** Shows a spinner on Save and blocks both buttons. */
  isSaving?: boolean
  /**
   * Block Save while the form is dirty but not yet valid. The bar still
   * appears, so the operator can see there is pending work and discard it.
   */
  saveDisabled?: boolean
  /** Left-hand text. Defaults to "You have unsaved changes". */
  message?: React.ReactNode
  saveLabel?: string
  savingLabel?: string
  discardLabel?: string
  /**
   * `sticky` (default) pins the bar to the bottom of the scrolling page.
   * `inline` drops it in the normal flow, for use inside a modal or a short
   * card where pinning would float over nothing.
   */
  placement?: 'sticky' | 'inline'
  className?: string
}

/**
 * The suite's one affordance for committing an explicit-Save settings form: a
 * bar that appears when the form goes dirty, carrying Save and Discard.
 *
 * It replaces the two shapes that had drifted apart — a banner at the top of
 * the page (too far from a field edited near the bottom) and a button parked
 * under the last field (below the fold on a long form). Sticking to the
 * viewport bottom keeps the commit in reach wherever the operator is editing.
 *
 * Use it for workspace-admin, validated, multi-field or irreversible settings.
 * Personal, per-device, cheaply-reversible settings (theme, notification
 * toggles, a profile photo) still save on change and must not grow a save bar —
 * see the settings save model in CLAUDE.md.
 *
 * WHERE TO PUT IT
 *
 * Two placement mistakes account for every one this component has shipped, and
 * neither shows up as an error — the bar just quietly sits in the wrong place.
 *
 * 1. Never render it as a direct grid item. Sticky positioning is bounded by
 *    the containing block, and a grid area is exactly as tall as the bar, so it
 *    never travels: it simply sits at the bottom of the page. Spanning columns
 *    does not help, because the containing block is still one row. Putting it
 *    INSIDE a grid item is fine and is usually right — the form's own column is
 *    tall enough for sticky to work and is already the correct width.
 *
 * 2. Make it as wide as the widest thing it commits — the form, not the page,
 *    and check the left edge as well as the width. "Is the bar inside the
 *    capped container?" is not the test and let this ship twice: it passes
 *    vacuously when the page root carries no cap and each section is capped
 *    instead, leaving a full-width bar under a `max-w-2xl` form.
 *    `EditAutomation`, `EditTrigger`, `EditView` and `EditMacro` all shipped
 *    that way; they now cap the whole page on `FormPageLayout` and render the
 *    bar inside it, so the bar inherits the form's width. Where the form sits
 *    in one column of a grid, put the bar in that column; where the form is a
 *    card, put it below the card at card width; where the form is a plain
 *    capped column, put the bar inside that same wrapper (`ProfileTab`,
 *    `OrganizationPage`, `CompanySettingsTab` and `WorkspaceSettingsTab` in
 *    dashboard each render it inside their `max-w-2xl` div).
 */
export function SettingsSaveBar({
  visible,
  onSave,
  onDiscard,
  isSaving = false,
  saveDisabled = false,
  message,
  saveLabel = 'Save',
  savingLabel,
  discardLabel = 'Discard',
  placement = 'sticky',
  className,
}: SettingsSaveBarProps) {
  if (!visible) return null

  return (
    <div
      role="region"
      aria-label={typeof message === 'string' ? message : 'Unsaved changes'}
      className={cn(
        placement === 'sticky' && 'sticky bottom-4 z-20 mt-6',
        placement === 'inline' && 'mt-6',
        className
      )}
    >
      <div
        className={cn(
          // No wrapping of the row itself: a long message wraps inside its
          // span and the buttons stay where they are, so they never jump to
          // a second line when the text changes.
          'flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3',
          'bg-card/95 supports-[backdrop-filter]:bg-card/80 supports-[backdrop-filter]:backdrop-blur',
          placement === 'sticky' && 'shadow-lg'
        )}
      >
        <span className="min-w-0 flex-1 text-sm text-muted-foreground">
          {message ?? 'You have unsaved changes'}
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {onDiscard && (
            <Button variant="outline" size="sm" onClick={onDiscard} disabled={isSaving}>
              {discardLabel}
            </Button>
          )}
          <LoadingButton
            size="sm"
            onClick={onSave}
            loading={isSaving}
            loadingText={savingLabel}
            disabled={saveDisabled}
          >
            {saveLabel}
          </LoadingButton>
        </div>
      </div>
    </div>
  )
}

export default SettingsSaveBar
